import React, { useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useDragContext } from './DragContext';

const ItemTypes = {
  TIMELINE_ITEM: 'TIMELINE_ITEM',
  PLANET_ACTION: 'PLANET_ACTION'
};

const DraggableActionItem = ({ 
  children, 
  index, 
  planetId, 
  moveItem, 
  actionData, 
  setDraggedItem, 
  updatePreviewState 
}) => {
  const ref = useRef(null);
  const { setIsDragging } = useDragContext();

  // Set up the drag source
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TIMELINE_ITEM,
    item: () => {
      // Notify parent about the dragged item
      const draggedItem = { 
        type: ItemTypes.TIMELINE_ITEM, 
        index, 
        planetId,
        actionData
      };
      // Set global dragging state to true
      setIsDragging(true);
      setDraggedItem(draggedItem);
      return draggedItem;
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    end: () => {
      // Clear dragged item when drag ends
      setDraggedItem(null);
      updatePreviewState(null);
      // Set global dragging state to false
      setIsDragging(false);
    }
  });

  // Set up the drop target
  const [, drop] = useDrop({
    accept: [ItemTypes.TIMELINE_ITEM, ItemTypes.PLANET_ACTION],
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }

      // Don't replace items with themselves
      if (item.type === ItemTypes.TIMELINE_ITEM) {
        if (item.planetId === planetId && item.index === index) {
          // When hovering over the original position, reset the preview to show original state
          updatePreviewState(null);
          return;
        }
      }

      // Get the rectangle of the current component
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      
      // Get the middle Y position
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      
      // Get the mouse position
      const clientOffset = monitor.getClientOffset();
      
      // Get the distance from the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      // Only perform the move when the mouse has crossed half of the item's height
      // This prevents excessive flickering when moving the mouse around
      if (item.type === ItemTypes.TIMELINE_ITEM && item.planetId === planetId) {
        if (
          (item.index < index && hoverClientY < hoverMiddleY) || 
          (item.index > index && hoverClientY > hoverMiddleY)
        ) {
          // We're not crossing the middle point yet, so don't update preview
          return;
        }
      }

      // Generate a preview of what would happen if dropped here
      updatePreviewState(item, planetId, index);
    },
    drop: (item) => {
      if (item.type === ItemTypes.TIMELINE_ITEM) {
        // Move within timeline
        const sourceIndex = item.index;
        const sourcePlanetId = item.planetId;
        
        // Don't replace items with themselves
        if (sourcePlanetId === planetId && sourceIndex === index) {
          return;
        }

        // Call the moveItem handler
        moveItem(sourcePlanetId, sourceIndex, planetId, index);
      } else if (item.type === ItemTypes.PLANET_ACTION) {
        // Add from planet display
        const { actionType, payload } = item;
        moveItem(null, null, planetId, index, { type: actionType, payload });
      }
      
      // Clear preview state
      updatePreviewState(null);
    }
  });

  // Apply the ref to both drag source and drop target
  drag(drop(ref));

  // No need to pass isDragging prop anymore since we're using context

  return (
    <div 
      ref={ref} 
      style={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move'
      }}
      className="draggable-item"
    >
      {children}
    </div>
  );
};

export { DraggableActionItem, ItemTypes };
